// Date helpers locked to Europe/Zurich. The app's "today" is the Swiss day,
// not the device's UTC day or the device's local time (which can differ if a
// user travels).
//
// We always work with ISO date strings ('YYYY-MM-DD') for DB columns and
// equality comparisons. Date arithmetic uses UTC midpoint to avoid DST edge
// cases (DST shifts only ±1h, never enough to flip a day from 12:00 UTC).

const CH_TIMEZONE = 'Europe/Zurich';

const ISO_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: CH_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: CH_TIMEZONE,
  weekday: 'short',
});

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/**
 * Returns today's date in Europe/Zurich as 'YYYY-MM-DD'.
 * Use this for any DB column that stores a `date` (action_date, quiz date,
 * streak last_action_date, etc.).
 */
export function getTodayCH(): string {
  return ISO_DATE_FORMATTER.format(new Date());
}

/**
 * Returns yesterday's date in Europe/Zurich as 'YYYY-MM-DD'.
 * Used for streak continuity checks.
 */
export function getYesterdayCH(): string {
  return shiftDayString(getTodayCH(), -1);
}

/**
 * Returns the day-of-week index (Sun=0, Mon=1, ..., Sat=6) for "today" in
 * Europe/Zurich. Matches the convention of JS `Date.getDay()`.
 */
export function getDayOfWeekCH(): number {
  const wd = WEEKDAY_FORMATTER.format(new Date()) as keyof typeof WEEKDAY_INDEX;
  return WEEKDAY_INDEX[wd] ?? 0;
}

/**
 * Returns the Monday of the current ISO week in Europe/Zurich as 'YYYY-MM-DD'.
 * ISO week starts on Monday.
 */
export function getMondayOfWeekCH(): string {
  const today = getTodayCH();
  const dow = getDayOfWeekCH();
  const daysSinceMonday = (dow + 6) % 7; // Mon=0, Tue=1, ..., Sun=6
  return shiftDayString(today, -daysSinceMonday);
}

/**
 * Returns the Friday (or Sunday, depending on use case) of the current week
 * starting from Monday.
 */
export function shiftDayString(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  // Use noon UTC as anchor to avoid DST flipping the day.
  const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().split('T')[0];
}

/**
 * Returns the ISO date string for an arbitrary Date object, expressed in
 * Europe/Zurich.
 */
export function toDateStringCH(date: Date): string {
  return ISO_DATE_FORMATTER.format(date);
}
